import React from 'react';
import {AssignmentRounded, DescriptionRounded, EditRounded, MenuBookRounded, PlayCircleRounded, QuizRounded, SearchRounded, SportsEsportsRounded} from '@mui/icons-material';
import BlockIcon from './components/BlockIcon';
import TextbookSectionCard from './components/TextbookSectionCard';
import ExerciseCard from './components/ExerciseCard';
import WalkthroughCard from './components/WalkthroughCard';
import InteractiveCard from './components/InteractiveCard';
import VideoCard from './components/VideoCard';
import WorksheetCard from './components/WorksheetCard';

const MOCK_SECTION_DATA = [
    {
        key: 'textbookContent',
        icon: <BlockIcon icon={<MenuBookRounded />} />,
        label: 'Examples',
        items: [
            { id: 'tb1', title: 'Example 1A.1 — Converting Roman numerals to Hindu-Arabic', topPick: true, summary: 'Introduces the Roman numeral system and maps I, V, X, L, C, D, M to their Hindu-Arabic equivalents.', pagePill: 'p. 4' },
            { id: 'tb2', title: 'Example 1B.3 — Identifying place value in 5-digit numbers', topPick: true, summary: 'Students identify the value of each digit in a 5-digit number, including the ten-thousands place.', pagePill: 'p. 9' },
            { id: 'tb3', title: 'Example 1C.2 — Column addition with carrying', topPick: true, summary: 'Step-by-step column addition with regrouping across tens and hundreds.', pagePill: 'p. 14' },
            { id: 'tb4', title: 'Example 1D.1 — Long multiplication with 3-digit numbers', topPick: false, summary: 'Extends multiplication to 3-digit by 2-digit using the standard algorithm.', pagePill: 'p. 19' },
            { id: 'tb5', title: 'Example 1E.2 — Short division with remainders', topPick: false, summary: 'Covers short division and how to express remainders as whole numbers or fractions.', pagePill: 'p. 24' },
            { id: 'tb6', title: 'Example 1F.1 — BODMAS with mixed operations', topPick: false, summary: 'Applies the BODMAS rule to expressions with multiple operations including powers.', pagePill: 'p. 29' },
        ],
        renderItem: (item, checked, onToggle) => (
            <TextbookSectionCard key={item.id} id={item.id} title={item.title} selected={checked} onToggle={onToggle} summary={item.summary} pagePill={item.pagePill} />
        ),
    },
    {
        key: 'exercise',
        icon: <BlockIcon icon={<EditRounded />} />,
        label: 'Exercise',
        previewable: true,
        items: [
            { id: 'ex1', title: 'Exercise 1A — Place value and number systems', topPick: true, chips: [{ label: 'Fluency', count: 6, variant: 'fluency' }, { label: 'Problem Solving', count: 2, variant: 'problem-solving' }] },
            { id: 'ex2', title: 'Exercise 1B — Addition and subtraction', topPick: true, chips: [{ label: 'Fluency', count: 8, variant: 'fluency' }, { label: 'Reasoning', count: 2, variant: 'reasoning' }] },
            { id: 'ex3', title: 'Exercise 1C — Multiplication tables review', topPick: false, chips: [{ label: 'Fluency', count: 6, variant: 'fluency' }] },
            { id: 'ex4', title: 'Exercise 1D — Division with remainders', topPick: false, chips: [{ label: 'Fluency', count: 5, variant: 'fluency' }, { label: 'Problem Solving', count: 3, variant: 'problem-solving' }] },
        ],
        renderItem: (item, checked, onToggle, onPreview) => (
            <ExerciseCard key={item.id} id={item.id} title={item.title} selected={checked} onToggle={onToggle} chips={item.chips} previewButton onPreview={onPreview} />
        ),
    },
    {
        key: 'questions',
        icon: <BlockIcon icon={<QuizRounded />} />,
        label: 'Questions',
        items: [],
    },
    {
        key: 'quiz',
        icon: <BlockIcon icon={<AssignmentRounded />} />,
        label: 'Quiz',
        items: [
            { id: 'qz1', title: 'Place Value Quick Check', topPick: true, chips: [{ label: 'Fluency', count: 3, variant: 'fluency' }, { label: 'Reasoning', count: 2, variant: 'reasoning' }] },
            { id: 'qz2', title: 'Integer Operations Quiz', topPick: true, chips: [{ label: 'Fluency', count: 5, variant: 'fluency' }, { label: 'Problem Solving', count: 3, variant: 'problem-solving' }] },
            { id: 'qz3', title: 'Number Systems Review', topPick: false, chips: [{ label: 'Fluency', count: 4, variant: 'fluency' }, { label: 'Enrichment', count: 2, variant: 'enrichment' }] },
        ],
        renderItem: (item, checked, onToggle, onPreview) => (
            <ExerciseCard key={item.id} id={item.id} title={item.title} selected={checked} onToggle={onToggle} chips={item.chips} previewButton onPreview={onPreview} />
        ),
    },
    {
        key: 'walkthroughs',
        icon: <BlockIcon icon={<SearchRounded />} />,
        label: 'Walkthroughs',
        previewable: true,
        items: [
            { id: 'wt1', title: 'Walkthrough 1 — Reading large numbers', topPick: true, problemPreview: 'Write 4 305 672 in words and identify the value of the digit 3.', stepTrack: 3 },
            { id: 'wt2', title: 'Walkthrough 2 — Column addition with regrouping', topPick: true, problemPreview: 'Calculate 3 847 + 2 965 using the column method.', stepTrack: 4 },
            { id: 'wt3', title: 'Walkthrough 3 — Long multiplication', topPick: false, problemPreview: 'Evaluate 342 × 27 using the standard algorithm.', stepTrack: 5 },
        ],
        renderItem: (item, checked, onToggle, onPreview) => (
            <WalkthroughCard key={item.id} id={item.id} title={item.title} selected={checked} onToggle={onToggle} problemPreview={item.problemPreview} stepTrack={item.stepTrack} previewButton onPreview={onPreview} />
        ),
    },
    {
        key: 'extras',
        icon: <BlockIcon icon={<SportsEsportsRounded />} />,
        label: 'Scorcher, Desmos and Games',
        previewable: true,
        items: [
            { id: 'sc1', title: 'A 7-digit number has a 4 in the millions place. What is the smallest such number?', topPick: true, typePill: 'Scorcher' },
            { id: 'sc2', title: 'Place value sliders and visual exploration', topPick: true, typePill: 'Desmos' },
            { id: 'sc3', title: 'Place Value Bingo', topPick: false, typePill: 'Game' },
            { id: 'sc4', title: 'Explain why the digit 0 is essential in a positional number system.', topPick: false, typePill: 'Scorcher' },
        ],
        renderItem: (item, checked, onToggle, onPreview) => (
            <InteractiveCard key={item.id} id={item.id} title={item.title} selected={checked} onToggle={onToggle} typePill={item.typePill} previewButton onPreview={onPreview} />
        ),
    },
    {
        key: 'videos',
        icon: <BlockIcon icon={<PlayCircleRounded />} />,
        label: 'Videos',
        items: [
            { id: 'v1', title: 'Introduction to Place Value', topPick: true, meta: 'Worked example', durationPill: '4:32' },
            { id: 'v2', title: 'Hindu-Arabic Number System Explained', topPick: true, meta: 'Concept explainer', durationPill: '6:15' },
            { id: 'v3', title: 'Column Addition — Worked Example', topPick: false, meta: 'Worked example', durationPill: '3:48' },
        ],
        renderItem: (item, checked, onToggle) => (
            <VideoCard key={item.id} id={item.id} title={item.title} selected={checked} onToggle={onToggle} meta={item.meta} durationPill={item.durationPill} />
        ),
    },
    {
        key: 'worksheets',
        icon: <BlockIcon icon={<DescriptionRounded />} />,
        label: 'Worksheets',
        items: [
            { id: 'ws1', title: 'Place Value — Reading and writing large numbers', topPick: true, summary: 'Covers reading and writing numbers up to 7 digits, expanded form, and standard form.', taskPill: '3 tasks' },
            { id: 'ws2', title: 'Integer Operations — Addition and subtraction practice', topPick: true, summary: 'Mixed practice on column addition and subtraction with regrouping up to 6-digit numbers.', taskPill: '2 tasks' },
            { id: 'ws3', title: 'Mixed Operations — Word problems', topPick: false, summary: 'Real-world word problems applying all four operations with multi-digit numbers.', taskPill: '4 tasks' },
        ],
        renderItem: (item, checked, onToggle) => (
            <WorksheetCard key={item.id} id={item.id} title={item.title} selected={checked} onToggle={onToggle} summary={item.summary} taskPill={item.taskPill} />
        ),
    },
];

export default MOCK_SECTION_DATA;
